import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { backendUrl, API_KEYS } from '../config';
import { Box, Paper, Typography, TextField, Button, List, ListItem, ListItemIcon, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import FolderIcon from '@mui/icons-material/Folder';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import StorageIcon from '@mui/icons-material/Storage';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CalculateIcon from '@mui/icons-material/Calculate';
import PublicIcon from '@mui/icons-material/Public';
import NoteIcon from '@mui/icons-material/Note';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';

// Fonction utilitaire pour encoder une image en base64
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Fonction utilitaire pour uploader un fichier JS depuis le backend (via le nouvel endpoint download) ---
const uploadAppCodeFromBackend = async (appId) => {
  // Récupère le code JS via /applications/download/{app_id}
  const resp = await fetch(`${backendUrl}/applications/download/${appId}`, {
    headers: {
      'x-api-key': API_KEYS.key,
      'x-api-secret': API_KEYS.secret
    }
  });
  if (!resp.ok) throw new Error('Fichier non trouvé côté backend pour appId: ' + appId);
  const fileContent = await resp.text();
  // Déduit le nom du fichier depuis le header Content-Disposition
  const disposition = resp.headers.get('Content-Disposition');
  let fileName = 'app_code.js';
  if (disposition && disposition.includes('filename=')) {
    fileName = disposition.split('filename=')[1].replace(/"/g, '');
  }
  // POST upload-code avec le contenu récupéré
  const response = await fetch(`${backendUrl}/applications/upload-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEYS.key,
      'x-api-secret': API_KEYS.secret
    },
    body: JSON.stringify({
      fileName: fileName,
      content: fileContent,
      appId: appId
    })
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error('Erreur backend: ' + err);
  }
  return await response.json();
};

// --- Fonction pour télécharger le code JS d'une application par son ID ---
const downloadAppCode = async (appId) => {
  const response = await fetch(`${backendUrl}/applications/download/${appId}`, {
    headers: {
      'x-api-key': API_KEYS.key,
      'x-api-secret': API_KEYS.secret
    }
  });
  if (!response.ok) {
    throw new Error('Erreur lors du téléchargement du code : ' + await response.text());
  }
  // Récupère le nom de fichier depuis le header Content-Disposition
  const disposition = response.headers.get('Content-Disposition');
  let fileName = 'app_code.js';
  if (disposition && disposition.includes('filename=')) {
    fileName = disposition.split('filename=')[1].replace(/"/g, '');
  }
  const blob = await response.blob();
  // Déclenche le téléchargement dans le navigateur
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Affichage du nom avec majuscule au début
function displayAppName(name) {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Fonction utilitaire pour upload l'icône personnalisée :
async function uploadCustomIcon(label, value, file) {
  const formData = new FormData();
  formData.append('label', label);
  formData.append('value', value);
  formData.append('file', file);
  const res = await fetch(`${backendUrl}/icons/icon`, {
    method: 'PUT',
    body: formData
  });
  if (!res.ok) throw new Error(t('common.error.uploadIcon'));
  return await res.json();
}

export default function ApplicationSettingsPage() {
  const { t } = useTranslation();
  const [apps, setApps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [form, setForm] = useState({ name: "", type: "custom", icon: "apps", url: "", icon_data: "", width: '', height: '' });
  const [iconFile, setIconFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importConfigAndCodeOpen, setImportConfigAndCodeOpen] = useState(false);
  const [importConfigFile, setImportConfigFile] = useState(null);
  const [importCodeFile, setImportCodeFile] = useState(null);
  const [importName, setImportName] = useState("");
  const [iconOptions, setIconOptions] = useState([]);
  const fetchApps = () => {
    fetch(`${backendUrl}/applications/`, {
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret
      }
    })
    .then(res => res.json())
    .then(setApps)
    .catch(err => console.error('Error fetching apps:', err));
  };

  useEffect(() => {
    // Charger dynamiquement la liste des icônes depuis le backend
    fetch(`${backendUrl}/icons/icons`)
      .then(res => res.json())
      .then(data => setIconOptions(data))
      .catch(() => setIconOptions([]));
  }, []);

  useEffect(() => {
    fetchApps();
  }, []);

  // Export d'une application individuelle (config simple ou config + code selon le type)
  const handleExportApp = async (app) => {
    if (app.type === 'url') {
      // Export simple (config seule)
      const blob = new Blob([JSON.stringify(app, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `application_url_${app.name.replace(/\s+/g, '_').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Export config + code, fichiers séparés, l'un après l'autre
      // Déterminer le(s) vrai(s) fichier(s) JS associé(s) à l'application
      let fileName = '';
      let fallbackNames = [];
 fileName = `${app.name.replace(/\s+/g, '')}App.js`;
      let code = '';
      let foundFile = fileName;
      // Essayer le fichier principal, puis les fallback si nécessaire
      const tryFiles = [fileName, ...fallbackNames];
      for (const candidate of tryFiles) {
        try {
          const res = await fetch(`${backendUrl}/applications/download/${app.id}`, {
            headers: {
              'x-api-key': API_KEYS.key,
              'x-api-secret': API_KEYS.secret
            }
          });
          if (res.ok) {
            code = await res.text();
            foundFile = candidate;
            break;
          }
        } catch (e) {}
      }
      // 1. Export config (sans le champ code)
      const { id, ...configData } = app;
      const configBlob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const configUrl = URL.createObjectURL(configBlob);
      const configFileName = `application_config_${app.name.replace(/\s+/g, '_').toLowerCase()}.json`;
      const a1 = document.createElement('a');
      a1.href = configUrl;
      a1.download = configFileName;
      a1.click();
      URL.revokeObjectURL(configUrl);
      // 2. Export code (fichier JS brut)
      if (foundFile && code) {
        const codeBlob = new Blob([code], { type: 'text/javascript' });
        const codeUrl = URL.createObjectURL(codeBlob);
        const codeFileName = foundFile;
        const a2 = document.createElement('a');
        a2.href = codeUrl;
        a2.download = codeFileName;
        a2.click();
        URL.revokeObjectURL(codeUrl);
      }
    }
  };

  // Import config + code séparés pour une application (nom partout adapté)
  const handleImportConfigAndCode = async (configFile, codeFile, importName) => {
    if (!configFile || !codeFile) {
      alert("Veuillez sélectionner un fichier de config ET un fichier de code.");
      return;
    }
    try {
      // 1. Lecture du fichier config (JSON)
      const configText = await configFile.text();
      let appData = JSON.parse(configText);
      const { id, ...appDataNoId } = appData;
      // 2. Lecture du code source (JS)
      let codeText = await codeFile.text();
      // 3. Vérifier qu'il n'y a qu'un seul fichier code JS
      if (codeText.includes('export default') && codeText.match(/export default/g).length > 1) {
        alert('Le code contient plusieurs modules export default. Veuillez importer un seul fichier de code JS par application.');
        return;
      }
      // 4. Renommage du nom partout si demandé
      let newName = importName && importName.trim() ? importName.trim().toLowerCase() : appDataNoId.name.toLowerCase();
      appDataNoId.name = newName;
      // 5. Détermination du nom de fichier unique et canonique
      let fileName = '';
      if (newName === 'fureteur chrome') fileName = 'Browser.js';
      else if (newName === 'calculatrice') fileName = 'CalculatorApp.js';
      else if (newName === 'bloc-notes' || newName === 'blocknote') fileName = 'NotepadApp.js';
      else if (newName === 'explorateur de fichiers') fileName = 'FileExplorerApp.js';
      else if (newName === 'explorateur simple') fileName = 'FileExplorer.js';
      else fileName = `${newName.replace(/\s+/g, '')}App.js`;
      // 6. Création de l'entrée application
      const res = await fetch(`${backendUrl}/applications/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...appDataNoId, name: newName })
      });
      const savedApp = await res.json();
      // 7. Enregistrement du code source dans un seul fichier (FRONTEND)
      await fetch(`${backendUrl}/applications/upload-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret
        },
        body: JSON.stringify({
          fileName: fileName,
          content: codeText,
          appId: savedApp.id
        })
      });
      // 8. Écrire le code JS dans le répertoire src/apps côté frontend avec le même nom que sur le backend
      try {
        await fetch(`${backendUrl}/api/write-to-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetPath: `/home/bob/CascadeProjects/desktop/webdesk-frontend/src/apps/${fileName}`,
            content: codeText
          })
        });
      } catch (err) {
        console.error("Erreur lors de l'écriture locale du code JS dans src/apps:", err);
      }
      // 9. (Optionnel) Envoi du code source au backend pour stockage dans /app (BACKEND)
      try {
        await fetch(`${backendUrl}/applications/upload-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEYS.key,
            'x-api-secret': API_KEYS.secret
          },
          body: JSON.stringify({
            fileName: fileName,
            content: codeText
          })
        });
      } catch (err) {
        // Optionnel : notifier l'utilisateur si l'import backend échoue
        console.error('Erreur lors de l\'envoi du code au backend :', err);
      }
      fetchApps();
    } catch (e) {
      alert('Erreur lors de l\'import : ' + e.message);
    }
  };

  // Suppression d'une application et de son code associé (frontend + backend)
  const handleDelete = async (idx) => {
    const app = apps[idx];
    // 1. Déterminer le nom du fichier code associé à l'application
    let fileName = '';
    if (app.name === 'Fureteur Chrome') fileName = 'Browser.js';
    else if (app.name === 'Calculatrice') fileName = 'CalculatorApp.js';
    else if (app.name === 'Bloc-notes' || app.name === 'Blocknote') fileName = 'NotepadApp.js';
    else if (app.name === 'Explorateur de fichiers') fileName = 'FileExplorerApp.js';
    else if (app.name === 'Explorateur simple') fileName = 'FileExplorer.js';
    else fileName = `${app.name.replace(/\s+/g, '')}App.js`;

    // 2. Supprimer le code côté backend via API
    if (fileName) {
      try {
        await fetch(`${backendUrl}/applications/delete-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEYS.key,
            'x-api-secret': API_KEYS.secret
          },
          body: JSON.stringify({ fileName })
        });
      } catch (err) {
        console.error("Erreur lors de la suppression du code backend:", err);
      }
    }

    // 3. Supprimer l'application côté backend (API REST)
    try {
      await fetch(`${backendUrl}/applications/${app.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error("Erreur lors de la suppression de l'application:", err);
    }

    // 4. Rafraîchir la liste des applications
    fetchApps();
  };

  const handleOpenDialog = (idx = -1) => {
    setEditIndex(idx);
    if (idx >= 0) setForm(apps[idx]);
    else setForm({ name: "", type: "custom", icon: "apps", url: "", icon_data: "", width: '', height: '' });
    setIconFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditIndex(-1); };

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Gère l'upload de fichier icône
  const handleIconFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    // Utilise le nom du fichier sans extension comme value
    const value = file.name.replace(/\.[^/.]+$/, "");
    try {
      const icon = await uploadCustomIcon('Icône personnalisée', value, file);
      setForm(f => ({ ...f, icon: icon.value, icon_data: icon.icon_data }));
      setIconOptions(opts => [...opts.filter(o => o.value !== icon.value), icon]); // évite les doublons
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    let data = { ...form };
    if (iconFile) {
      data.icon_data = await fileToBase64(iconFile);
      data.icon = iconFile.name.replace(/\.[^/.]+$/, "");
    }
    // S'assurer que width/height sont bien des nombres ou undefined
    if (data.width) data.width = parseInt(data.width, 10);
    if (data.height) data.height = parseInt(data.height, 10);
    const method = editIndex >= 0 ? 'PUT' : 'POST';
    const url = editIndex >= 0 ? `/applications/${apps[editIndex].id}` : '/applications/';
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
      .then(res => res.json())
      .then(newApp => {
        if (editIndex >= 0) {
          setApps(apps.map((a, i) => i === editIndex ? newApp : a));
        } else {
          setApps([...apps, newApp]);
        }
        handleCloseDialog();
      })
      .finally(() => setLoading(false));
  };

  // Pré-remplissage rapide pour Chrome
  const handleQuickAddChrome = () => {
    setForm({
      name: "Fureteur Chrome",
      type: "browser",
      icon: "chrome",
      url: "google-chrome",
      icon_data: "",
      width: '',
      height: ''
    });
    setDialogOpen(true);
    setEditIndex(-1);
    setIconFile(null);
  };

  // Pré-remplissage rapide pour Chrome (stockage style "store")
  const handleQuickAddChromeStore = () => {
    setForm({
      name: "Fureteur Chrome",
      type: "browser",
      icon: "chrome",
      url: "https://www.google.com",
      icon_data: "",
      width: '',
      height: ''
    });
    setDialogOpen(true);
    setEditIndex(-1);
    setIconFile(null);
  };

  const handleImportConfigAndCodeDialogOpen = () => {
    setImportConfigAndCodeOpen(true);
    setImportConfigFile(null);
    setImportCodeFile(null);
    setImportName("");
  };
  const handleImportConfigAndCodeDialogClose = () => setImportConfigAndCodeOpen(false);
  const handleImportConfigAndCodeDialogConfigFile = (e) => setImportConfigFile(e.target.files[0] || null);
  const handleImportConfigAndCodeDialogCodeFile = (e) => setImportCodeFile(e.target.files[0] || null);
  const handleImportConfigAndCodeDialogSubmit = async () => {
    if (!importConfigFile || !importCodeFile) return;
    try {
      await handleImportConfigAndCode(importConfigFile, importCodeFile, importName);
      setImportConfigAndCodeOpen(false);
    } catch (e) {
      alert('Erreur lors de l\'import : ' + e.message);
    }
  };

  // Fonction utilitaire pour obtenir l'icône à partir du champ icon (string)
  const getAppIcon = (icon, icon_data) => {
    if (icon_data) {
      return <img src={icon_data} alt="icon" style={{width: 32, height: 32, objectFit: 'contain', verticalAlign: 'middle'}} />;
    }
    // Normalisation insensible à la casse pour la recherche dans iconOptions
    const normalizedIcon = icon ? icon.toLowerCase() : '';
    // Recherche dans window.iconOptions si disponible (pour cohérence avec DesktopPage)
    if (window.iconOptions && Array.isArray(window.iconOptions)) {
      const opt = window.iconOptions.find(o => o.value.toLowerCase() === normalizedIcon);
      if (opt && opt.icon_data) {
        return <img src={opt.icon_data} alt={opt.label} style={{width: 32, height: 32, objectFit: 'contain', verticalAlign: 'middle'}} />;
      }
    } else if (iconOptions && iconOptions.length > 0) {
      const opt = iconOptions.find(o => o.value.toLowerCase() === normalizedIcon);
      if (opt && opt.icon_data) {
        return <img src={opt.icon_data} alt={opt.label} style={{width: 32, height: 32, objectFit: 'contain', verticalAlign: 'middle'}} />;
      }
    }
    // Sinon fallback sur les icônes locales
    switch (normalizedIcon) {
      case 'explorer':
      case 'folder':
      case 'directory':
        return <FolderIcon style={{color: '#2196f3'}} />;
      case 'calculator':
        return <CalculateIcon style={{color: '#43a047'}} />;
      case 'browser':
        return <PublicIcon style={{color: '#1976d2'}} />;
      case 'notepad':
        return <NoteIcon style={{color: '#fbc02d'}} />;
      case 'recyclebin':
        return <DeleteIcon style={{color: '#b71c1c'}} />;
      case 'images':
        return <ImageIcon style={{color: '#ab47bc'}} />;
      case 'videos':
        return <MovieIcon style={{color: '#009688'}} />;
      case 'email':
        return <EmailIcon style={{color: '#1976d2'}} />;
      case 'printer':
        return <PrintIcon style={{color: '#616161'}} />;
      case 'desktop':
        return <DesktopWindowsIcon style={{color: '#1976d2'}} />;
      case 'settings':
        return <SettingsIcon style={{color: '#616161'}} />;
      case 'users':
        return <PeopleIcon style={{color: '#795548'}} />;
      case 'security':
        return <SecurityIcon style={{color: '#b71c1c'}} />;
      case 'appearance':
        return <ColorLensIcon style={{color: '#ab47bc'}} />;
      case 'storage':
        return <StorageIcon style={{color: '#009688'}} />;
      case 'file':
        return <InsertDriveFileIcon style={{color: '#607d8b'}} />;
      default:
        return <AppsIcon />;
    }
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', boxSizing: 'border-box', p: 3 }}>
      <Typography variant="h5" mb={2}>{t('application.management.title')}</Typography>
      <Button variant="outlined" color="primary" onClick={handleImportConfigAndCodeDialogOpen} sx={{ mb: 2, mr: 2 }}>{t('application.import.title')}</Button>
      <List>
        {apps.length === 0 && <Typography color="text.secondary">Aucune application configurée.</Typography>}
        {apps.map((app, idx) => (
          <ListItem key={app.id || idx}>
            <ListItemIcon>
              {getAppIcon(app.icon, app.icon_data)}
            </ListItemIcon>
            <ListItemText
              primary={displayAppName(app.name)}
              secondary={app.type === 'url' ? 'Lien URL' : app.type === 'store' ? 'Application store' : 'Application custom'}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              onClick={() => handleExportApp(app)}
            >{t('common.export')}</Button>
            <IconButton edge="end" aria-label="edit" onClick={() => handleOpenDialog(idx)}><EditIcon /></IconButton>
            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(idx)}><DeleteIcon /></IconButton>
          </ListItem>
        ))}
      </List>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{editIndex >= 0 ? t('application.edit') : t('application.add')} une application</DialogTitle>
        <DialogContent sx={{ minWidth: 320 }}>
          <TextField label="Nom" name="name" value={form.name} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Type" name="type" value={form.type} onChange={handleChange} fullWidth margin="normal" select>
            <MenuItem value="custom">Custom</MenuItem>
            {/* Ajouter d'autres types si besoin */}
          </TextField>
          <FormControl fullWidth margin="normal" disabled={!!iconFile}>
            <InputLabel id="icon-select-label">Icône</InputLabel>
            <Select
              labelId="icon-select-label"
              name="icon"
              value={form.icon}
              label="Icône"
              onChange={handleChange}
              renderValue={selected => {
                if (selected === 'custom' && form.icon_data) {
                  return <span style={{display:'flex',alignItems:'center',gap:8}}><img src={form.icon_data} alt="icon" style={{width:24,height:24,objectFit:'contain',verticalAlign:'middle'}}/> {t('application.customIcon')}</span>;
                }
                const opt = iconOptions.find(o => o.value === selected);
                return opt ? <span style={{display:'flex',alignItems:'center',gap:8}}><img src={opt.icon_data} alt={opt.label} style={{width:24,height:24,objectFit:'contain',verticalAlign:'middle'}}/> {opt.label}</span> : selected;
              }}
            >
              {iconOptions.map(opt => (
                <MenuItem value={opt.value} key={opt.value}>
                  <span style={{display:'flex',alignItems:'center',gap:8}}><img src={opt.icon_data} alt={opt.label} style={{width:24,height:24,objectFit:'contain',verticalAlign:'middle'}}/> {opt.label}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button component="label" variant="outlined" sx={{ mt: 1, mb: 1 }}>
            {iconFile ? iconFile.name : t('application.uploadIcon')}
            <input type="file" accept="image/svg+xml,image/png" hidden onChange={handleIconFileChange} />
          </Button>
          <TextField label="Largeur (px)" name="width" type="number" value={form.width || ''} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Hauteur (px)" name="height" type="number" value={form.height || ''} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="URL/Commande" name="url" value={form.url} onChange={handleChange} fullWidth margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>{loading ? t('common.saving') : t('common.save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={importConfigAndCodeOpen} onClose={handleImportConfigAndCodeDialogClose}>
        <DialogTitle>{t('application.import.title')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label={t('application.import.name')} value={importName} onChange={e => setImportName(e.target.value)} fullWidth />
            <Button variant="outlined" component="label">
              {t('application.import.configFile')}
              <input type="file" accept="application/json" hidden onChange={handleImportConfigAndCodeDialogConfigFile} />
            </Button>
            {importConfigFile && <Typography variant="body2">{importConfigFile.name}</Typography>}
            <Button variant="outlined" component="label">
              {t('application.import.codeFile')}
              <input type="file" accept=".js" hidden onChange={handleImportConfigAndCodeDialogCodeFile} />
            </Button>
            {importCodeFile && <Typography variant="body2">{importCodeFile.name}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportConfigAndCodeDialogClose}>{t('common.cancel')}</Button>
          <Button onClick={handleImportConfigAndCodeDialogSubmit} variant="contained" disabled={!importConfigFile || !importCodeFile}>{t('application.import.action')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
